import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ResourceCategory } from '@/contexts/ResourceContext';

const categoryConfig: Record<ResourceCategory, { label: string; icon: string; color: string }> = {
  food: { label: 'Food & Groceries', icon: '🍎', color: '#22c55e' },
  shelter: { label: 'Shelter & Housing', icon: '🏠', color: '#8b5cf6' },
  medical: { label: 'Health Care', icon: '⚕️', color: '#ef4444' },
  skills: { label: 'Skills & Learning', icon: '📚', color: '#3b82f6' },
  tools: { label: 'Tools & Equipment', icon: '🔧', color: '#f59e0b' },
  transport: { label: 'Transportation', icon: '🚗', color: '#06b6d4' },
  legal: { label: 'Legal Aid', icon: '⚖️', color: '#ec4899' },
  childcare: { label: 'Childcare', icon: '👶', color: '#f97316' },
  elderlycare: { label: 'Elder Care', icon: '👵', color: '#6366f1' },
  'mental-health': { label: 'Mental Health', icon: '💜', color: '#a855f7' },
  community: { label: 'Community', icon: '🌱', color: '#14b8a6' },
  other: { label: 'Other', icon: '✨', color: '#6b7280' },
};

interface CategoryFilterProps {
  selected: ResourceCategory[];
  onChange: (categories: ResourceCategory[]) => void;
  className?: string;
}

export function CategoryFilter({ selected, onChange, className = '' }: CategoryFilterProps) {
  const categories = Object.keys(categoryConfig) as ResourceCategory[];

  const toggleCategory = (category: ResourceCategory) => {
    if (selected.includes(category)) {
      onChange(selected.filter((c) => c !== category));
    } else {
      onChange([...selected, category]);
    }
  };

  const clearAll = () => onChange([]);

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">Filter by category</h3>
        {selected.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={clearAll}
          >
            Clear all
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => {
          const config = categoryConfig[category];
          const isSelected = selected.includes(category);

          return (
            <Button
              key={category}
              variant={isSelected ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'h-8 px-3 transition-all duration-200',
                isSelected && 'scale-105 shadow-md'
              )}
              style={
                isSelected
                  ? {
                      backgroundColor: config.color,
                      borderColor: config.color,
                    }
                  : {}
              }
              onClick={() => toggleCategory(category)}
            >
              <span className="mr-1.5">{config.icon}</span>
              <span className="text-xs">{config.label}</span>
            </Button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="flex items-center gap-2 pt-1">
          <Badge variant="secondary" className="text-xs">
            {selected.length} selected
          </Badge>
        </div>
      )}
    </div>
  );
}

// Compact version for sidebar/header
export function CategoryPills({
  categories,
  className,
}: {
  categories: ResourceCategory[];
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {categories.map((category) => {
        const config = categoryConfig[category];
        return (
          <Badge
            key={category}
            variant="secondary"
            className="text-xs font-normal px-2 py-0.5"
            style={{
              backgroundColor: `${config.color}15`,
              color: config.color,
            }}
          >
            {config.icon} {config.label}
          </Badge>
        );
      })}
    </div>
  );
}
